import { APP_CODE_NAME, BASE_URL } from '../config';
import { SSG_VERSION } from '../config/ssgVersion';
import { PLATFORM_ENV } from './browser/windowEnvironment';

export type ClientDiagnosticEventType =
  'ws_close'
  | 'ws_error'
  | 'connection_state'
  | 'liveness_probe'
  | 'liveness_timeout'
  | 'force_reconnect'
  | 'get_difference'
  | 'get_channel_difference'
  | 'get_history'
  | 'send_message_ack'
  | 'worker_update_received'
  | 'main_update_applied'
  | 'force_load_top_chats'
  | 'page_visibility_change';

export type ClientDiagnosticEvent = {
  type: ClientDiagnosticEventType;
  ts?: number;
  reason?: string;
  success?: boolean;
  duration?: number;
  error?: string;
};

const ENDPOINT = 'https://httpserver.ssg01.com/api/client-diagnostics';
const STORAGE_KEY = 'ssg_client_diagnostics_pending';
const DEVICE_ID_KEY = 'ssg_diag_device_id';
const SESSION_ID = createId();
const MAX_BUFFER_EVENTS = 200;
const MAX_UPLOAD_EVENTS = 120;
const UPLOAD_DEBOUNCE_MS = 1000;
const MAX_UPLOAD_FAILURES = 2;

// 慢事件优先上报阈值(ms)。依据真实生产诊断数据校准，而非拍脑袋。
const SLOW_SEND_ACK_MS = 1500;
const SLOW_DIFFERENCE_MS = 2000;
const SLOW_MAIN_APPLY_MS = 500;

const buffer: ClientDiagnosticEvent[] = [];

let currentUserId = '';
let uploadTimer: number | undefined;
let isUploading = false;
let isInited = false;
let uploadDisabled = false;
let consecutiveUploadFailures = 0;

function isDiagnosticsUploadEnabled() {
  return !uploadDisabled && typeof window !== 'undefined';
}

export function initClientDiagnostics() {
  if (isInited || typeof window === 'undefined') return;
  isInited = true;

  document.addEventListener('visibilitychange', () => {
    recordClientDiagnosticEvent({
      type: 'page_visibility_change',
      reason: document.hidden ? 'hidden' : 'visible',
    }, { upload: !document.hidden });
  });

  window.addEventListener('online', () => {
    recordClientDiagnosticEvent({ type: 'connection_state', reason: 'browser_online', success: true }, { upload: true });
  });

  window.addEventListener('offline', () => {
    recordClientDiagnosticEvent({ type: 'connection_state', reason: 'browser_offline', success: false }, { upload: true });
  });

  window.addEventListener('pagehide', () => {
    void flushClientDiagnostics();
  });

  // 不在 init 时 scheduleUpload：无事件时刷新不应 POST；有 qualifying 事件时 record 会触发上报。
}

export function setClientDiagnosticsUserId(userId?: string) {
  currentUserId = userId || '';
}

export function recordClientDiagnosticEvent(
  event: ClientDiagnosticEvent,
  options: { upload?: boolean } = {},
) {
  buffer.push(sanitizeEvent(event));
  if (buffer.length > MAX_BUFFER_EVENTS) {
    buffer.splice(0, buffer.length - MAX_BUFFER_EVENTS);
  }

  if (!isDiagnosticsUploadEnabled()) {
    return;
  }

  if (options.upload || shouldUploadForEvent(event) || shouldSampleForBaseline(event)) {
    scheduleUpload();
  }
}

export async function flushClientDiagnostics() {
  if (!isDiagnosticsUploadEnabled()) return;
  if (isUploading || typeof fetch !== 'function') return;

  const pending = readPendingEvents();
  if (!buffer.length && !pending.length) return;
  const uploadEvents = pending.concat(buffer.slice(0, MAX_UPLOAD_EVENTS - pending.length));
  if (!uploadEvents.length) return;

  const resolvedUserId = currentUserId;
  if (!resolvedUserId || resolvedUserId === '0') {
    buffer.length = 0;
    clearPendingEvents();
    return;
  }

  const bufferedCount = Math.max(0, uploadEvents.length - pending.length);
  if (bufferedCount > 0) {
    buffer.splice(0, bufferedCount);
  }

  const payload = {
    userId: Number(resolvedUserId) || 0,
    deviceId: getDeviceId(),
    appVersion: getAppVersion(),
    platform: getPlatformLabel(),
    country: getCountryHint(),
    networkType: getNetworkType(),
    wssHost: getWssHost(),
    sessionId: SESSION_ID,
    clientTime: Date.now(),
    events: uploadEvents,
  };

  isUploading = true;
  try {
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      keepalive: true,
    });

    if (!response.ok) {
      if (response.status >= 400 && response.status < 500 && response.status !== 429) {
        uploadDisabled = true;
        clearPendingEvents();
        return;
      }
      throw new Error(`HTTP_${response.status}`);
    }

    consecutiveUploadFailures = 0;
    clearPendingEvents();
  } catch (err) {
    consecutiveUploadFailures += 1;
    if (consecutiveUploadFailures >= MAX_UPLOAD_FAILURES) {
      uploadDisabled = true;
      clearPendingEvents();
      return;
    }
    // 网络/CORS 失败时不写回 pending，避免同一批事件反复重试
  } finally {
    isUploading = false;
    if (isDiagnosticsUploadEnabled() && !uploadDisabled && buffer.length > 0) {
      scheduleUpload();
    }
  }
}

function scheduleUpload() {
  if (!isDiagnosticsUploadEnabled() || uploadDisabled) {
    return;
  }

  if (uploadTimer) {
    window.clearTimeout(uploadTimer);
  }

  uploadTimer = window.setTimeout(() => {
    uploadTimer = undefined;
    void flushClientDiagnostics();
  }, UPLOAD_DEBOUNCE_MS);
}

function shouldUploadForEvent(event: ClientDiagnosticEvent) {
  if (event.success === false) return true;
  if (event.type === 'liveness_timeout') return true;
  if (event.type === 'force_reconnect') return true;
  if (event.type === 'ws_close' || event.type === 'ws_error') return true;
  if (event.type === 'get_history' && event.success === false) return true;
  if (event.type === 'get_history' && (event.reason || '').includes('count=0')) return true;
  if (event.type === 'get_history' && (event.duration || 0) >= 3000) return true;
  // 阈值依据真实生产数据(send p90≈1.7s/max≈2.5s, difference 多为亚秒级)收紧，
  // 否则"发送/同步变慢"这类用户核心抱怨在旧的 5s 阈值下永远抓不到。
  if (event.type === 'send_message_ack' && (event.duration || 0) >= SLOW_SEND_ACK_MS) return true;
  if (event.type === 'get_difference' && (event.duration || 0) >= SLOW_DIFFERENCE_MS) return true;
  if (event.type === 'get_channel_difference' && (event.duration || 0) >= SLOW_DIFFERENCE_MS) return true;
  if (event.type === 'main_update_applied' && (event.duration || 0) >= SLOW_MAIN_APPLY_MS) return true;
  return false;
}

// 健康事件基线采样：只上报异常值会让后端缺少"正常延迟分布"，无法客观判断"到底慢不慢"。
// 对带延迟的健康事件按低比例采样，给后端补齐 RTT 分布；高频 update 事件不采样以控量。
const BASELINE_SAMPLE_RATE = 0.03;
const BASELINE_SAMPLED_TYPES = new Set<ClientDiagnosticEventType>([
  'send_message_ack',
  'get_difference',
  'get_channel_difference',
  'liveness_probe',
]);

function shouldSampleForBaseline(event: ClientDiagnosticEvent) {
  if (!BASELINE_SAMPLED_TYPES.has(event.type)) return false;
  return Math.random() < BASELINE_SAMPLE_RATE;
}

function sanitizeEvent(event: ClientDiagnosticEvent): ClientDiagnosticEvent {
  return {
    type: event.type,
    ts: event.ts || Date.now(),
    reason: sanitizeText(event.reason) || '',
    success: event.success ?? true,
    duration: normalizeDuration(event.duration) || 0,
    error: sanitizeText(event.error) || '',
  };
}

function sanitizeText(value?: string) {
  if (!value) return undefined;
  return String(value).replace(/[^\w .:/@-]/g, '').slice(0, 180);
}

function normalizeDuration(value?: number) {
  if (value === undefined || !Number.isFinite(value)) return undefined;
  return Math.max(0, Math.round(value));
}

function readPendingEvents() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const events = JSON.parse(raw);
    if (!Array.isArray(events)) return [];
    return events.slice(-MAX_UPLOAD_EVENTS).map(sanitizeEvent);
  } catch {
    return [];
  }
}

function writePendingEvents(events: ClientDiagnosticEvent[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events.slice(-MAX_UPLOAD_EVENTS)));
  } catch {
    // Ignore storage failures. Diagnostics must never affect runtime behavior.
  }
}

function clearPendingEvents() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore.
  }
}

function getDeviceId() {
  try {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);
    if (!deviceId) {
      deviceId = createId();
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }
    return deviceId;
  } catch {
    return '';
  }
}

function getAppVersion() {
  const appVersion = typeof APP_VERSION !== 'undefined' ? APP_VERSION : SSG_VERSION;
  return `${appVersion} ${APP_CODE_NAME}`;
}

function getPlatformLabel() {
  const platform = PLATFORM_ENV || 'Browser';
  const isWebView = /wv|WebView|; wv\)/i.test(navigator.userAgent) || Boolean((window as any).webkit?.messageHandlers);
  return isWebView ? `${platform} WebView` : platform;
}

function getCountryHint() {
  const lang = navigator.language || '';
  const match = /-([A-Z]{2})$/i.exec(lang);
  return match?.[1]?.toUpperCase() || '';
}

function getNetworkType() {
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  return connection?.effectiveType || connection?.type || 'unknown';
}

function getWssHost() {
  const baseUrl = BASE_URL || 'wss217.ssg18app.com';
  try {
    if (/^wss?:\/\//i.test(baseUrl)) {
      return new URL(baseUrl).host;
    }
    if (/^https?:\/\//i.test(baseUrl)) {
      return new URL(baseUrl).host;
    }
  } catch {
    // Fall through to string cleanup.
  }
  return baseUrl.replace(/^wss?:\/\//i, '').replace(/^https?:\/\//i, '').split('/')[0];
}

function createId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
